import * as React from 'react';

import * as ArrayUtils from '@acceligentllc/shared/utils/array';
import * as TimeUtils from '@acceligentllc/shared/utils/time';

import UI_LABELS from 'af-constants/uiLabels';

import Dropdown from 'af-components/Controls/Dropdown';

import { NUMBER_OF_FUTURE_YEAR_OPTIONS, YEAR_OPTION_START } from './constants';
import styles from './styles.module.scss';

type MomentType = ReturnType<typeof TimeUtils.parseMoment>;

interface DropdownItem {
	id: number;
	label: string;
}

const MONTH_OPTIONS: DropdownItem[] = Object.keys(UI_LABELS.MONTH).map((_month, _index) => ({
	id: _index,
	label: UI_LABELS.MONTH[_month] as string,
}));

const YEAR_OPTIONS: DropdownItem[] = ArrayUtils
	.range(YEAR_OPTION_START, new Date().getFullYear() + 1 + NUMBER_OF_FUTURE_YEAR_OPTIONS)
	.map((_year: number, _index: number) => ({ id: _index, label: _year.toString() }));

interface OwnProps {
	selected: Date;
	onChange: (selected: Date, startDate: Date, endDate: Date) => void;
}

const MonthlyPicker: React.FC<OwnProps> = ({ selected: selectedDate, onChange }) => {
	const [selected, setSelected] = React.useState<MomentType>(TimeUtils.parseMoment(selectedDate));

	React.useEffect(() => {
		setSelected(TimeUtils.parseMoment(selectedDate));
	}, [selectedDate]);

	const onMonthChange = React.useCallback((item: DropdownItem) => {
		if (!selected) {
			throw new Error('No date used as reference point');
		}
		const newSelected = selected.clone().month(item?.id ?? 0); // this null check is in case for January, which has id 0 and that causes problems
		onChange(
			newSelected.toDate(),
			newSelected.clone().startOf('month').toDate(),
			newSelected.clone().endOf('month').toDate()
		);
		setSelected(newSelected);
	}, [selected, onChange]);

	const onYearChange = React.useCallback((item: DropdownItem) => {
		if (!selected) {
			throw new Error('No date used as reference point');
		}
		const newSelected = selected.clone().year(+item.label);
		onChange(
			newSelected.toDate(),
			newSelected.clone().startOf('month').toDate(),
			newSelected.clone().endOf('month').toDate()
		);
		setSelected(newSelected);
	}, [selected, onChange]);

	const renderDropdownElement = React.useCallback((item: DropdownItem) => {
		return item ? <div className={styles['dropdown-item']}>{item.label}</div> : <div />;
	}, []);

	const renderSelectedYearElement = React.useCallback((item: DropdownItem) => {
		return (
			<div className={styles['selected-fiscal-year']}>
				{item.label}
				<span className="icon-calendar" />
			</div>
		);
	}, []);

	return (
		<div className={styles['date-picker-wrapper']}>
			<Dropdown<DropdownItem>
				containerClassName={styles['date-picker-wrapper-monthly-month']}
				defaultValue={{ id: selected!.month(), label: UI_LABELS.MONTH[selected!.month() + 1] }}
				id="month"
				labelKey="label"
				onValueChange={onMonthChange}
				options={MONTH_OPTIONS}
				renderMenuItem={renderDropdownElement}
				valueKey="id"
				withBorder={false}
			/>
			<Dropdown<DropdownItem>
				containerClassName={styles['date-picker-wrapper-monthly-year']}
				defaultValue={{ id: 0, label: selected!.year().toString() }}
				id="year"
				labelKey="label"
				onValueChange={onYearChange}
				options={YEAR_OPTIONS}
				renderMenuItem={renderDropdownElement}
				renderSelected={renderSelectedYearElement}
				valueKey="id"
				withBorder={false}
			/>
		</div>
	);
};

export default React.memo(MonthlyPicker);
